import { InboxOutlined } from "@ant-design/icons";
import type { IntlShape } from "@formatjs/intl";
import type { Dispatch } from "@reduxjs/toolkit";
import { Upload, type UploadProps } from "antd";
import type { HookAPI } from "antd/es/modal/useModal";
import type { UploadChangeParam, UploadFile } from "antd/es/upload";
import { useMemo } from "react";
import { FileListItem } from "../../../common/components/FileListItem";
import { startUpload } from "../../files/store";
import type { ModelFile } from "../../generated-axios-client";
import styles from "./TaskUploadSection.module.css";

type TaskUploadSectionProps = {
  intl: IntlShape;
  dispatch: Dispatch;
  fieldPrefix: number;
  onChange?: (value: Record<string, unknown>) => void;
  notifficationApi?: HookAPI;
};

export function TaskUploadSection({
  intl,
  dispatch,
  fieldPrefix,
  onChange,
  notifficationApi,
}: TaskUploadSectionProps) {
  const uploadConfig: UploadProps = useMemo(
    () => ({
      multiple: true,
      action: undefined,
      customRequest: (options) => {
        if (options.file instanceof File) {
          dispatch(startUpload(options.file, options.onSuccess, options.onError));
        } else {
          notifficationApi?.warning({ title: intl.formatMessage({ id: "upload_failed" }) });
        }
      },
      onChange: (info: UploadChangeParam<UploadFile<ModelFile>>) => {
        if (!onChange) return;
        const newFiles = info.fileList
          .filter((f) => f.status === "done" && f.response)
          .reduce(
            (obj, f) => {
              if (f.response?.file_path) {
                obj[f.response.file_path] = {
                  name: f.response.name,
                  size: f.response.size,
                  content_type: f.response.content_type,
                  path: f.response.file_path,
                };
              }
              return obj;
            },
            {} as Record<string, unknown>,
          );
        onChange(newFiles);
      },
    }),
    [dispatch, intl, notifficationApi, onChange],
  );

  return (
    <Upload.Dragger
      {...uploadConfig}
      className={styles.uploadWrapper}
      itemRender={(_, file, __, actions) => (
        <FileListItem
          file={file}
          onRemove={actions.remove}
          intl={intl}
          fieldPrefix={fieldPrefix}
          showDescription
        />
      )}
    >
      <p className="ant-upload-drag-icon">
        <InboxOutlined />
      </p>
      <p className="ant-upload-text">{intl.formatMessage({ id: "upload_choose_files" })}</p>
      <p className="ant-upload-hint">{intl.formatMessage({ id: "upload_hint" })}</p>
    </Upload.Dragger>
  );
}
